import React from "react";
import Icon from "assets/icons";
import classNames from "classnames";
import { PaymentMethod } from "./enum";
import "@reach/menu-button/styles.css";
import Button from "shared/components/button";
import { positionRight } from "@reach/popover";
import { PaymentWrapperView } from "../../enum";
import { useCopies } from "shared/hooks/useCopies";
import { usePaymentWrapperHeaderService } from "./service";
import { Menu, MenuButton, MenuItem, MenuPopover } from "@reach/menu-button";
import { PaymentWrapperHeaderPropTypes, UpdateOrResendRequestActionPropTypes } from "./types";

function PaymentWrapperHeader({ shouldShowPaymentModeOptions = true, ...props }: PaymentWrapperHeaderPropTypes) {
  return (
    <React.Fragment>
      <HeaderActions {...props} shouldShowPaymentModeOptions={shouldShowPaymentModeOptions} />
      <MessageActions {...props} />
    </React.Fragment>
  );
}

export default PaymentWrapperHeader;

function HeaderActions(props: PaymentWrapperHeaderPropTypes) {
  const copies = useCopies();
  const service = usePaymentWrapperHeaderService(props);

  const offlinePaymentRequestedAndPaymentModesAvailable =
    props.offlinePaymentConfig?.offlinePaymentRequested && props.shouldShowPaymentModeOptions;

  return (
    <div className="paymentWrapper_header">
      <div className="paymentWrapper_headerLeft">
        <h3 className="paymentWrapper_headerLeft--title">
          {copies.get(
            props.state.paymentWrapperView === PaymentWrapperView.REQUEST_PAYMENT
              ? "invoice.payment.form.label.send.request"
              : offlinePaymentRequestedAndPaymentModesAvailable
              ? "offline.payment.requested.payment.method.header.label"
              : props.state.paymentWrapperView === PaymentWrapperView.OFFLINE_PAYMENT
              ? "offline.payment.method.header.label"
              : "payment.method"
          )}
        </h3>
      </div>
      {service.applicablePaymentMethod !== PaymentMethod.NONE && (
        <div className="paymentWrapper_headerRight">
          <div className="paymentWrapper_headerRight--invPayerMsg">
            <HeaderActionsComponent {...props} />
          </div>
        </div>
      )}
    </div>
  );
}

export function HeaderActionsComponent(props: PaymentWrapperHeaderPropTypes) {
  const copies = useCopies();
  const service = usePaymentWrapperHeaderService(props);

  if (props.offlinePaymentConfig?.offlinePaymentRequested || !props.shouldShowPaymentModeOptions) return null;

  switch (service.applicablePaymentMethod) {
    case PaymentMethod.BOTH:
      return <HeaderActionsForMultiplePaymentMethods {...props} />;
    case PaymentMethod.INVOICE_PAYMENT:
      return (
        <React.Fragment>
          {props.state.paymentWrapperView === PaymentWrapperView.PAY_BY_SELF &&
            !props.invoicePaymentConfig?.initialEmail &&
            copies.get("invoice.payment.request.payment.info.msg")}
          {props.state.paymentWrapperView === PaymentWrapperView.REQUEST_PAYMENT &&
            copies.get("invoice.payment.back.to.payment.info.msg")}
          {!(
            props.state.paymentWrapperView === PaymentWrapperView.PAY_BY_SELF &&
            props.invoicePaymentConfig?.initialEmail
          ) && <Button.Link label={copies.get("click.here")} onClick={props.toggleInvoicePayment} />}
        </React.Fragment>
      );
    case PaymentMethod.OFFLINE_PAYMENT:
      return (
        <React.Fragment>
          {props.state.paymentWrapperView === PaymentWrapperView.OFFLINE_PAYMENT
            ? copies.get("offline.payment.back.to.payment.info.msg")
            : props.offlinePaymentConfig?.offlinePaymentRequested
            ? null
            : copies.get("offline.payment.mode.pay.offline.action.info.msg")}
          {!(
            props.state.paymentWrapperView === PaymentWrapperView.PAY_BY_SELF &&
            props.offlinePaymentConfig?.offlinePaymentRequested
          ) && <Button.Link label={copies.get("click.here")} onClick={props.toggleOfflinePayment} />}
        </React.Fragment>
      );
    default:
      return null;
  }
}

function HeaderActionsForMultiplePaymentMethods(props: PaymentWrapperHeaderPropTypes) {
  const copies = useCopies();

  const menuItems = React.useMemo(() => {
    let items = [];
    if (
      (props.state.paymentWrapperView === PaymentWrapperView.PAY_BY_SELF ||
        props.state.paymentWrapperView === PaymentWrapperView.OFFLINE_PAYMENT) &&
      (!props.invoicePaymentConfig?.initialEmail || props.offlinePaymentConfig?.offlinePaymentRequested)
    ) {
      items.push({
        displayOrder: props.state.paymentWrapperView === PaymentWrapperView.PAY_BY_SELF ? 0 : 2,
        label: copies.get("multiple.payment.modes.invoice.payment.request.payment.info.msg"),
        singleActionLabel: copies.get("invoice.payment.request.payment.info.msg"),
        clickHandler: props.toggleInvoicePayment
      });
    }
    if (props.state.paymentWrapperView === PaymentWrapperView.REQUEST_PAYMENT) {
      items.push({
        displayOrder: 1,
        label: copies.get("multiple.payment.modes.invoice.payment.back.to.payment.info.msg"),
        singleActionLabel: copies.get("invoice.payment.back.to.payment.info.msg"),
        clickHandler: props.toggleInvoicePayment
      });
    }

    if (
      (props.state.paymentWrapperView === PaymentWrapperView.OFFLINE_PAYMENT &&
        props.offlinePaymentConfig?.offlinePaymentRequested) ||
      (props.offlinePaymentEnabled && !props.offlinePaymentConfig?.offlinePaymentRequested)
    ) {
      items.push({
        displayOrder: props.state.paymentWrapperView === PaymentWrapperView.OFFLINE_PAYMENT ? 0 : 1,
        label: copies.get(
          props.state.paymentWrapperView === PaymentWrapperView.OFFLINE_PAYMENT
            ? "multiple.payment.modes.offline.payment.back.to.payment.info.msg"
            : "multiple.payment.modes.offline.payment.mode.pay.offline.action.info.msg"
        ),
        singleActionLabel: copies.get(
          props.state.paymentWrapperView === PaymentWrapperView.OFFLINE_PAYMENT
            ? "offline.payment.back.to.payment.info.msg"
            : "offline.payment.mode.pay.offline.action.info.msg"
        ),
        clickHandler: props.toggleOfflinePayment
      });
    }
    return items.sort((a, b) => a.displayOrder - b.displayOrder);
  }, [
    copies,
    props.toggleInvoicePayment,
    props.toggleOfflinePayment,
    props.offlinePaymentEnabled,
    props.state.paymentWrapperView,
    props.invoicePaymentConfig?.initialEmail,
    props.offlinePaymentConfig?.offlinePaymentRequested
  ]);

  return (
    <React.Fragment>
      {menuItems.length === 1 ? (
        <React.Fragment>
          {menuItems[0].singleActionLabel}
          <Button.Link label={copies.get("click.here")} onClick={menuItems[0].clickHandler} />
        </React.Fragment>
      ) : (
        <div className="dropDnMenu menuActions">
          <Menu>
            <MenuButton className="menuActions_menuButton">
              <span className="menuActions_menuButton--text">{copies.get("other.ways.to.pay")}</span>
              <span className="menuActions_menuButton--icon">
                <Icon.MenuDots />
              </span>
            </MenuButton>
            <MenuPopover position={positionRight} className="slide-down newMenuDropdownList paymentModeOptions">
              <div className="newMenuDropdownList--container">
                <div className="newMenuDropdownList--items">
                  {menuItems.map((item, index) => (
                    <MenuItem key={index} onSelect={item.clickHandler}>
                      {item.label}
                    </MenuItem>
                  ))}
                </div>
              </div>
            </MenuPopover>
          </Menu>
        </div>
      )}
    </React.Fragment>
  );
}

function MessageActions(props: PaymentWrapperHeaderPropTypes) {
  const copies = useCopies();
  const service = usePaymentWrapperHeaderService(props);

  const isOfflinePaymentView = React.useMemo(
    () => props.state.paymentWrapperView === PaymentWrapperView.OFFLINE_PAYMENT,
    [props.state.paymentWrapperView]
  );

  return (
    <div className="paymentWrapper_messages">
      {props.invoicePaymentConfig?.initialEmail && props.state.canShowUpdateInvoicePaymentRequestAction && (
        <UpdateOrResendRequestAction
          alreadyRequestedInfoCopy={copies.get("invoice.payment.already.requested.to", {
            0: `<strong class="paymentWrapper_updateRequest--requestedToMsgEmail">${props.invoicePaymentConfig?.initialEmail}</strong>`
          })}
          alreadyRequestedActionCopy={copies.get("invoice.payment.request.update.resend.msg")}
          isActionButtonDisabled={!props.invoicePaymentEnabled}
          actionButtonDisabledReasonCopy={copies.get("invoice.payment.update.resend.disabled.reason")}
          actionClickHandler={service.updateInvoicePaymentRequestClickHandler}
          paySelfMessageCopy={
            isOfflinePaymentView ? undefined : copies.get("invoice.payment.already.requested.pay.self")
          }
        />
      )}
      {props.invoicePaymentConfig?.initialEmail && props.state.canShowResendInvoicePaymentRequestAction && (
        <UpdateOrResendRequestAction
          isResend
          alreadyRequestedInfoCopy={copies.get("invoice.payment.already.requested.to", {
            0: `<strong class="paymentWrapper_resendRequest--requestedToMsgEmail">${props.invoicePaymentConfig?.initialEmail}</strong>`
          })}
          isActionButtonDisabled={!props.invoicePaymentEnabled}
          actionButtonDisabledReasonCopy={copies.get("invoice.payment.update.resend.disabled.reason")}
          actionClickHandler={service.resendInvoicePaymentRequestClickHandler}
          paySelfMessageCopy={
            isOfflinePaymentView ? undefined : copies.get("invoice.payment.already.requested.update.info.message")
          }
        />
      )}
      {props.offlinePaymentConfig?.offlinePaymentRequested &&
        props.state.paymentWrapperView !== PaymentWrapperView.OFFLINE_PAYMENT_REQUESTED && (
          <UpdateOrResendRequestAction
            alreadyRequestedInfoCopy={copies.get(
              props.state.paymentWrapperView === PaymentWrapperView.PAY_BY_SELF
                ? "offline.payment.already.requested.info.message"
                : "offline.payment.already.requested.update.ref.info.message"
            )}
          />
        )}
    </div>
  );
}

function UpdateOrResendRequestAction(props: UpdateOrResendRequestActionPropTypes) {
  const copies = useCopies();

  const computedClassName = React.useMemo(
    () => (props.isResend ? "paymentWrapper_resendRequest" : "paymentWrapper_updateRequest"),
    [props.isResend]
  );

  return (
    <React.Fragment>
      <div className={computedClassName}>
        <div className={`${computedClassName}--requestedTo`}>
          <p className={`${computedClassName}--requestedToMsg`}>
            <Icon.InfoCircleThick />
            <span
              className={`${computedClassName}--requestedToMsgText`}
              dangerouslySetInnerHTML={{
                __html: props.alreadyRequestedInfoCopy
              }}
            />{" "}
            {props.alreadyRequestedActionCopy && props.alreadyRequestedActionCopy}
          </p>
          {props.actionClickHandler && (
            <span className={`${computedClassName}--requestedToAction`}>
              <Button.Link
                disabled={props.isActionButtonDisabled}
                icon={props.isResend && <Icon.ResendEmail />}
                onClick={props.actionClickHandler}
                title={props.isActionButtonDisabled ? props.actionButtonDisabledReasonCopy : ""}
                label={props.isResend ? copies.get("invoice.payment.resend.request.action") : copies.get("click.here")}
              />
            </span>
          )}
        </div>
        {Boolean(props.paySelfMessageCopy) && (
          <p
            className={classNames({
              [`${computedClassName}--paySelf`]: !props.isResend,
              [`${computedClassName}--requestedMsg`]: props.isResend
            })}>
            {props.paySelfMessageCopy}
          </p>
        )}
      </div>
    </React.Fragment>
  );
}
